import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type ElementName,
  ElementType,
  buildGroupsAndPeopleFromSelection,
  createQueryParamFromSelection,
} from '@secretescapes/cauldron';
import { i18n } from '@secretescapes/i18n';
import * as Sentry from '@sentry/react';
import { createMomentFromParam, formatMomentToString, isSameDay } from '@utils/dates.utils';
import { dispatchTrackingEvent } from '@utils/tracking.utils';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import { useTranslations } from '@utils/useTranslations';
import { FlightsSelectOption, useFlightState } from '@features/Flights';
import {
  type AvailabilityPresentationHandle,
  type DateSelectionTrackingDetails,
  FocusedInput,
  type TFocusedInput,
} from './AvailabilityPresentation.interfaces';
import {
  OffersWrapper,
  Presentation,
  RetryBoxWrapper,
  SelectionWrapper,
  StyledRetryBox,
} from './AvailabilityPresentation.styles';
import {
  ALL_OFFERS,
  deleteURLParams,
  determineMaxRoomCapacity,
  getCheapestFinalRates,
  getURLParams,
  notEmpty,
  urlParamsExist,
} from './AvailabilityPresentation.utils';
import { AvailabilitySearchBar } from './AvailabilitySearchBar';
import { type Option } from './AvailabilitySearchBar/DropdownInput';
import { type NullableMoment } from './Calendar';
import { CreditsStaffDiscountInfoBox } from './CreditsStaffDiscounts/CreditsStaffDiscountInfoBox';
import { navigatePostSale } from './Navigation.utils';
import { type ExtendedOffer, Offers } from './Offers';
import { useSelectionActions, useSelectionState } from './SelectionContext';
import FlightsSelectTabs from './Tabs/FlightSelectTabs';
import { useCalendar } from './useCalendar';
import { useFacets } from './useFacets';
import { useOffers } from './useOffers';
import { useReceipts } from './useReceipts';

interface Props {
  flightPriceAvailable: boolean;
  scrollToSection?: (isStickyNavInactive?: boolean) => void;
}

export const AvailabilityPresentation = forwardRef<AvailabilityPresentationHandle, Props>(
  ({ flightPriceAvailable, scrollToSection }: Props, ref) => {
    const { tripTypeParam, durationParam, airportCodeParam, airportNameParam } = getURLParams();

    const { saleId, setFinalRates } = useAppState();
    const { sale } = useServerState();
    const { territoryCode, affiliateCode, territoryLocale, currency, isMobile } =
      useApplicationState();
    const { translations } = useTranslations();
    const flightsEnabled = useFeatureFlag(BullfinchFeatureFlags.flightsEnabled);
    const { flightState, flightsSelected, setFlightsSelected, setFlightState } = useFlightState({
      flightsEnabled,
      sale,
      tripTypeParam,
      durationParam,
      airportCodeParam,
      airportNameParam,
    });
    const checkInDateParam = createMomentFromParam('checkin', territoryLocale);
    const checkOutDateParam = createMomentFromParam('checkout', territoryLocale);

    const { travellersSelection } = useSelectionState();
    const { changeOfferId } = useSelectionActions();

    const [checkInDate, setCheckInDate] = useState<NullableMoment>(
      checkInDateParam?.clone() ?? null
    );
    const [checkOutDate, setCheckOutDate] = useState<NullableMoment>(
      checkOutDateParam?.clone() ?? null
    );

    const [focusedInput, setFocusedInput] = useState<TFocusedInput>(null);
    const [animateOffersSelect, setAnimateOffersSelect] = useState(false);

    const dateSearchInitialised = useRef(false);

    const presentationContainerRef = useRef<HTMLDivElement | null>(null);

    const { people } = buildGroupsAndPeopleFromSelection(travellersSelection);

    const commonQueryVariables = {
      flightsSelected,
      flightState,
    };

    const { offers, offersLoading, offersError, offersRefetch } = useOffers();
    const { calendar, calendarLoading, calendarError, calendarRefetch } =
      useCalendar(commonQueryVariables);
    const { airports, nights, facetsOffers, facetsError, facetsLoading, facetsRefetch } =
      useFacets(commonQueryVariables);
    const { offerReceipts, receiptsLoading } = useReceipts({
      ...commonQueryVariables,
      checkInDateParam,
      checkOutDateParam,
    });

    const errors = offersError || calendarError || facetsError;
    const refetch = async () => {
      await Promise.all([offersRefetch(), calendarRefetch(), facetsRefetch()]);
    };

    useEffect(() => {
      if (!offerReceipts.length) {
        return;
      }

      const offersWithReceipts = offerReceipts.filter(or => !!or?.receipt);

      dispatchTrackingEvent<DateSelectionTrackingDetails>({
        name: 'dateSelection',
        contentElement: {
          elementName: 'offers' as ElementName,
          elementType: ElementType.NON_EXPIRED_SALE,
        },
        details: {
          specificDatesSearch: true,
          triggeredBy: 'sale_page_user',
          numResults: offersWithReceipts.length,
          parameters: {
            airport: flightsSelected ? flightState.selectedAirport.title : '',
            checkInDate: formatMomentToString(checkInDateParam),
            checkOutDate: formatMomentToString(checkOutDateParam),
            travellersSelection: createQueryParamFromSelection(travellersSelection),
          },
          results: offersWithReceipts.map(offerWithReceipt => ({
            offerId: offerWithReceipt?.id ?? '',
            price: offerWithReceipt?.receipt?.amountForDisplay ?? '',
            source: 'offerSearch',
          })),
        },
      });

      const receipts = offersWithReceipts.map(or => or?.receipt).filter(notEmpty);

      const rates = getCheapestFinalRates(
        receipts,
        flightsSelected ? 1 : travellersSelection.rooms.length,
        currency,
        territoryLocale
      );

      setFinalRates(prev => ({
        ...prev,
        show: true,
        cheapestFinalRate: rates?.cheapestFinalRate ?? '',
        cheapestFinalRatePerRoomPerNight: rates?.cheapestFinalRatePerRoomPerNight ?? '',
        numberOfTravellers: people.length,
        numberOfNights: receipts[0]?.nights ?? 1,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerReceipts]);

    const transformedOffers: ExtendedOffer[] = useMemo(() => {
      return offers
        .map(o => {
          const facetOffer = facetsOffers.find(fo => fo.id === o.id);

          return {
            ...o,
            receipt: offerReceipts.find(or => or.id === o.id)?.receipt,
            pricing: facetOffer
              ? { ...facetOffer, discountTooltip: sale?.prices?.discountTooltip }
              : undefined,
          };
        })
        .sort((a, b) => {
          const offerRateA = a.receipt?.amount ?? a.pricing?.leadRate?.amount ?? Infinity;
          const offerRateB = b.receipt?.amount ?? b.pricing?.leadRate?.amount ?? Infinity;

          return offerRateA - offerRateB;
        });
    }, [offers, offerReceipts, facetsOffers, sale?.prices?.discountTooltip]);

    useImperativeHandle(
      ref,
      () => ({
        openCalendar: () => {
          setFocusedInput(FocusedInput.StartDate);
        },
        container: presentationContainerRef,
      }),
      []
    );

    const handleAnimationChange = (newValue: boolean) => setAnimateOffersSelect(newValue);

    const openCalendar = (selectedOfferId: string) => {
      if (!isMobile) {
        scrollToSection?.();
      }

      changeOfferId(selectedOfferId);
      setAnimateOffersSelect(true);

      setFocusedInput(FocusedInput.StartDate);
    };

    const handleRetry = () => {
      if (urlParamsExist()) {
        deleteURLParams();
        window?.location?.reload();
      } else {
        void refetch();
      }
    };

    const offerOptions: Option[] = [
      { value: ALL_OFFERS, text: i18n('calendar.offer.dropdown.alloffers', [], translations) },
      ...transformedOffers
        .filter(offer => travellersSelection.rooms.length <= (offer?.pricing?.maxAvailability ?? 0))
        .map(offer => ({
          value: offer.id,
          text: offer.title ?? '',
        })),
    ];

    // A way to remove this useEffect in the future
    useEffect(() => {
      if (!dateSearchInitialised.current && !checkInDate) {
        setFinalRates(prev => ({ ...prev, flightsSelected }));
        dateSearchInitialised.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (offersLoading || (facetsLoading && !facetsOffers.length)) {
      return (
        <Skeleton
          flightPriceAvailable={flightPriceAvailable}
          flightsSelected={flightsSelected}
          isMobile={!!isMobile}
        />
      );
    }

    if (errors || !transformedOffers.length) {
      return (
        <RetryBoxWrapper data-testid="retry-box-avail-pres">
          <StyledRetryBox retryFunction={handleRetry} />
        </RetryBoxWrapper>
      );
    }

    const handleDatesChange = (
      changedCheckInDate: NullableMoment,
      changedCheckOutDate: NullableMoment
    ) => {
      setCheckInDate(changedCheckInDate);
      setCheckOutDate(changedCheckOutDate);
    };

    const isSameDates =
      checkInDate?.isSame(checkInDateParam, 'd') && checkOutDate?.isSame(checkOutDateParam, 'd');

    const handleBookClick = (selectedOfferId: string) => {
      if (!checkInDate || !checkOutDate || !saleId || !selectedOfferId) {
        return;
      }

      const clickedOffer = transformedOffers.find(offer => offer.id === selectedOfferId);

      if (!clickedOffer) {
        return;
      }

      const checkInLabel = checkInDate.locale(territoryLocale).format('YYYY-MM-DD');

      const checkOutLabel = checkOutDate.locale(territoryLocale).format('YYYY-MM-DD');

      navigatePostSale({
        withFlights: flightsSelected,
        startDate: checkInLabel,
        endDate: checkOutLabel,
        offerId: selectedOfferId,
        flightState,
        saleId,
        totalPrice: clickedOffer.receipt?.amount ?? 0,
        availableRooms: clickedOffer.receipt?.maxAvailability ?? 10,
        affiliate: affiliateCode,
        travellersSelectionParam: createQueryParamFromSelection(travellersSelection),
      });
    };

    const handleTabClick = (tabValue: FlightsSelectOption) => {
      const isHotelWithFlights = tabValue === FlightsSelectOption.hotelWithFlights;
      setFlightsSelected(isHotelWithFlights);
      setURLParams('tripType', tabValue);
      setFinalRates(prev => ({ ...prev, flightsSelected: isHotelWithFlights }));
    };

    const handleCalendarCancelled = () => {
      if (!isSameDay(checkInDate, checkInDateParam)) {
        setCheckInDate(checkInDateParam);
      }
      if (!isSameDay(checkOutDate, checkOutDateParam)) {
        setCheckOutDate(checkOutDateParam);
      }

      changeOfferId(ALL_OFFERS);

      if (isMobile) {
        scrollToSection?.(!!checkInDateParam);
      }
    };

    const isLoading = (facetsLoading && !offerReceipts.length) || receiptsLoading;

    const noAvailableOffers = transformedOffers.every(offer => !offer.pricing);
    const hasNoFlights = flightsSelected && !isLoading && noAvailableOffers;

    return (
      <Sentry.ErrorBoundary
        beforeCapture={scope => scope.setTag('error.boundary', 'avail-pres')}
        fallback={
          <RetryBoxWrapper data-testid="retry-box-avail-pres">
            <StyledRetryBox retryFunction={handleRetry} />
          </RetryBoxWrapper>
        }
      >
        <Presentation ref={presentationContainerRef}>
          <SelectionWrapper isMobile={isMobile}>
            {flightPriceAvailable && (
              <FlightsSelectTabs
                tripType={
                  flightsSelected
                    ? FlightsSelectOption.hotelWithFlights
                    : FlightsSelectOption.hotelOnly
                }
                onClick={handleTabClick}
                isAtolProtected={territoryCode === 'UK' && !isMobile}
              />
            )}
            <AvailabilitySearchBar
              availability={calendar}
              offerOptions={offerOptions}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              onDatesChange={handleDatesChange}
              roomCapacity={determineMaxRoomCapacity(transformedOffers)}
              isSameDates={isSameDates}
              focusedInput={focusedInput}
              setFocusedInput={setFocusedInput}
              onCalendarCancelled={handleCalendarCancelled}
              availabilityLoading={calendarLoading}
              withTabs={flightPriceAvailable}
              withFlights={flightsSelected}
              flightState={flightState}
              onFlightStateChange={setFlightState}
              airportOptions={airports}
              durationOptions={nights}
              scrollToCalendar={scrollToSection}
              animateOffersSelect={animateOffersSelect}
              onAnimationChange={handleAnimationChange}
              hasNoFlights={hasNoFlights}
            />
          </SelectionWrapper>
          <CreditsStaffDiscountInfoBox />
          <Offers
            offers={transformedOffers}
            isLoading={isLoading}
            onBookClick={handleBookClick}
            openCalendar={openCalendar}
            flightsSelected={flightsSelected}
            hasNoFlights={hasNoFlights}
          />
        </Presentation>
      </Sentry.ErrorBoundary>
    );
  }
);

interface SkeletonProps {
  flightPriceAvailable: boolean;
  flightsSelected: boolean;
  isMobile?: boolean;
}

const Skeleton = ({ flightPriceAvailable, flightsSelected, isMobile }: SkeletonProps) => {
  return (
    <Presentation data-testid="availability-skeleton">
      <SelectionWrapper isMobile={isMobile}>
        {flightPriceAvailable && <FlightsSelectTabs.Skeleton />}
        <AvailabilitySearchBar.Skeleton
          withTabs={flightPriceAvailable}
          flightsSelected={flightsSelected}
          isMobile={isMobile}
        />
      </SelectionWrapper>
      <OffersWrapper isMobile={isMobile}>
        <Offers.Skeleton showDiscount={!flightsSelected} showPhoto={false} numberOfCards={4} />
      </OffersWrapper>
    </Presentation>
  );
};

AvailabilityPresentation.displayName = 'AvailabilityPresentation';
